import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="pt-24 pb-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Effective Date: December 23, 2024
          </p>
          <p className="text-gray-600">
            Version: 1.0
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          <div className="space-y-8">
            <div className="bg-teal-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-teal-900 mb-3">Our Privacy Promise</h2>
              <p className="text-gray-700">
                At NeuPath, people come first. This Privacy Policy reflects our deep commitment to protecting 
                your personal information while providing insights that help individuals and organizations thrive 
                through change. We take your privacy seriously - it's not just compliance for us, it's core to who we are.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Gather (and Why)</h2>
              <p className="text-gray-600 mb-4">To understand and support your transformation journey, we collect:</p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">About You</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li><strong>Contact Information:</strong> Name, email, phone number, job title</li>
                <li><strong>Organization Details:</strong> Company name, size, industry, department, and role</li>
                <li><strong>Assessment Data:</strong> Your responses, profile type, readiness scores, and journey insights (for I AM Assessment users)</li>
                <li><strong>Engagement Information:</strong> How you interact with our website and platforms</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">Technical Information</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>IP address and browser information for security and functionality</li>
                <li>Device type and operating system</li>
                <li>Pages visited and interaction patterns</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">
                Everything we do with your information serves our mission of supporting people through change:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Providing personalized insights and recommendations</li>
                <li>Understanding organizational readiness patterns</li>
                <li>Delivering and improving our consulting services</li>
                <li>Communicating about our services and your engagement with us</li>
                <li>Maintaining a secure, trustworthy platform</li>
                <li>Complying with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Our AI: Working for You, Protecting You</h2>
              <p className="text-gray-600 mb-4">
                We use artificial intelligence to understand patterns and generate insights, but here's what makes us different:
              </p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Privacy-First AI Processing</h3>
              <p className="text-gray-600 mb-4">
                We redact and mask all personally identifiable information before any AI processing. Your name, 
                email, and other identifying details never reach our AI models. We analyze patterns and behaviors, 
                not people. Your responses help us understand change readiness, but your identity remains protected throughout.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Your data serves only one purpose:</strong> generating insights for you and your organization. 
                We never use your responses to train general AI models or for any purpose beyond your engagement with us.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Who We Share With (and Who We Don't)</h2>
              <p className="text-gray-600 mb-4">
                We're selective about sharing because your trust matters:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li><strong>Your Organization:</strong> For sponsored assessments, leaders see aggregated insights and patterns - never individual responses</li>
                <li><strong>Service Providers:</strong> Trusted partners who help us operate our platforms (under strict confidentiality)</li>
                <li><strong>Legal Requirements:</strong> Only when law requires it</li>
              </ul>
              <p className="text-gray-700 font-bold text-lg">
                We never sell your information. Period.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Protecting What You've Shared</h2>
              <p className="text-gray-600 mb-2">Your security is paramount:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Military-grade encryption for data in transit and at rest</li>
                <li>Secure cloud infrastructure (Google Cloud Platform)</li>
                <li>Strict access controls - only those who need to know</li>
                <li>Regular security reviews and updates</li>
                <li>Complete PII redaction before any AI processing</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Information Timeline</h2>
              <p className="text-gray-600 mb-4">
                We keep your information for 2 years - long enough to support your transformation journey, 
                not longer than necessary. After that, it's securely deleted unless regulations require otherwise.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights, Your Control</h2>
              <p className="text-gray-600 mb-4">
                This is your journey, and you have control:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>View your information and assessment results</li>
                <li>Correct any personal information</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Ask questions about how we protect you</li>
                <li>Request a copy of your data (data portability)</li>
              </ul>
              <p className="text-gray-600">
                Just reach out to <a href="mailto:privacy@neupath.ai" className="text-teal-600 hover:text-teal-700">privacy@neupath.ai</a> - we're here to help.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Cookies: Just the Essentials</h2>
              <p className="text-gray-600 mb-4">
                We use only essential cookies to maintain your session and ensure our platforms work smoothly. 
                No tracking cookies, no advertising cookies - just what's needed for a great experience. 
                You can control cookies through your browser settings.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-600 mb-4">
                Our website may contain links to third-party websites and services. We are not responsible 
                for the privacy practices of these third parties, and we encourage you to review their privacy policies.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Across Borders, Same Protection</h2>
              <p className="text-gray-600 mb-4">
                Sometimes data processing happens in different countries. Wherever your data goes, 
                our protection standards follow.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Young People</h2>
              <p className="text-gray-600 mb-4">
                Our services are designed for working professionals. We don't knowingly collect 
                information from anyone under 18.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Our Legal Foundation</h2>
              <p className="text-gray-600 mb-2">We process your information based on:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Your consent when you engage with our services</li>
                <li>Contractual agreements with organizations</li>
                <li>Our legitimate interests in helping people navigate change successfully</li>
                <li>Legal obligations when applicable</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Evolving with You</h2>
              <p className="text-gray-600 mb-4">
                As we grow and improve, this policy may update. We'll always notify you of significant changes - 
                transparency is part of our people-first commitment.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Real People, Real Support</h2>
              <p className="text-gray-600 mb-4">
                Questions about privacy? Concerns? We're here:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>NeuPath</strong><br />
                  Email: <a href="mailto:privacy@neupath.ai" className="text-teal-600 hover:text-teal-700">privacy@neupath.ai</a><br />
                  Website: <a href="https://www.neupath.ai" className="text-teal-600 hover:text-teal-700">www.neupath.ai</a>
                </p>
              </div>
            </div>

            <div className="bg-teal-50 p-6 rounded-lg mt-8">
              <p className="text-gray-700 italic">
                Remember: You're not a number to us. You're a person navigating change, and we're honored to 
                support your journey. Every privacy decision we make starts with one question: "Does this put 
                people first?"
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;